import { cn } from "../../lib/utils";

export interface ContentBlockProps {
  id?: string;
  className?: string;
  heading: string;
  content: string;
  imageUrl?: string;
  imageAlt?: string;
  layout: "image-left" | "image-right" | "centered" | "no-image" | "statement";
  backgroundColor?: "white" | "gray" | "primary";
  headingSize?: "large" | "medium" | "small";
  /** Framing for the image. "none" is right for logos and marks that sit on the page. */
  imageStyle?: "shadow" | "border" | "none";
}

export function ContentBlock({
  id,
  className,
  heading,
  content,
  imageUrl,
  imageAlt,
  layout = "image-left",
  backgroundColor = "white",
  headingSize = "large",
  imageStyle = "shadow",
}: ContentBlockProps) {
  const bgColorClasses = {
    white: "jobsite:bg-background",
    gray: "jobsite:bg-muted/50",
    primary: "jobsite:bg-primary/5",
  };

  const headingSizeClasses = {
    large: "jobsite:text-3xl jobsite:sm:text-4xl",
    medium: "jobsite:text-2xl jobsite:sm:text-3xl",
    small: "jobsite:text-xl jobsite:sm:text-2xl",
  };

  const showImage = layout !== "no-image" && layout !== "statement" && imageUrl;
  const surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const;

  // Tolerate a ReactNode in `content`/`heading`: the editors swap the string
  // for an inline-editable element. Strings keep the exact old semantics
  // (blank-line paragraph split); an element renders as one block.
  const paragraphs = typeof content === "string" ? content.split("\n\n").filter(Boolean) : null;

  const imageElement = showImage && (
    <img
      crossOrigin="anonymous"
      src={imageUrl}
      alt={imageAlt || (typeof heading === "string" ? heading : "")}
      className={cn(
        "jobsite:w-full jobsite:h-auto",
        imageStyle === "border" && "jobsite:border jobsite:border-border",
        imageStyle === "none" ? "" : imageStyle === "border" ? "" : "jobsite:shadow-lg",
      )}
      style={surfaceRadiusStyle}
    />
  );

  return (
    <section
      id={id}
      className={cn("jobsite:py-16 jobsite:md:py-24", bgColorClasses[backgroundColor], className)}
      // Explicit White must stay white: the inline style outranks the section
      // alternation rule in index.css, which keys on the bg-background class.
      style={
        backgroundColor === "white"
          ? { backgroundColor: "var(--jobsite-background)" }
          : undefined
      }
    >
      <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
        {layout === "statement" ? (
          // Editorial statement: the heading is the display element, prose answers it
          // alongside. The heading font and colors come from the theme, so the same
          // layout reads corporate in a sans theme and luxurious in a serif one.
          <div className="jobsite:mx-auto jobsite:grid jobsite:max-w-6xl jobsite:gap-8 jobsite:md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] jobsite:md:gap-16">
            <h2 className="jobsite:font-heading jobsite:text-4xl jobsite:leading-[1.1] jobsite:tracking-tight jobsite:text-balance jobsite:text-foreground jobsite:sm:text-5xl">
              {heading}
            </h2>
            <div className="jobsite:space-y-4 jobsite:text-lg jobsite:leading-relaxed jobsite:text-muted-foreground jobsite:md:pt-2">
              {paragraphs ? (
                paragraphs.map((paragraph, idx) => <p key={idx}>{paragraph}</p>)
              ) : (
                <p>{content}</p>
              )}
            </div>
          </div>
        ) : layout === "centered" ? (
          <div className="jobsite:max-w-3xl jobsite:mx-auto jobsite:text-center">
            {imageElement && <div className="jobsite:mb-10">{imageElement}</div>}
            <h2
              className={cn(
                "jobsite:font-heading jobsite:mb-5 jobsite:text-foreground",
                headingSizeClasses[headingSize],
              )}
            >
              {heading}
            </h2>
            <div className="jobsite:space-y-4 jobsite:text-lg jobsite:leading-relaxed jobsite:text-muted-foreground">
              {paragraphs ? (
                paragraphs.map((paragraph, idx) => <p key={idx}>{paragraph}</p>)
              ) : (
                <p>{content}</p>
              )}
            </div>
          </div>
        ) : (
          <div
            className={cn(
              "jobsite:flex jobsite:flex-col jobsite:items-center jobsite:gap-8 jobsite:md:gap-16",
              layout === "image-right" ? "jobsite:md:flex-row-reverse" : "jobsite:md:flex-row",
            )}
          >
            {imageElement && <div className="jobsite:w-full jobsite:md:w-1/2">{imageElement}</div>}
            <div
              className={cn(
                showImage
                  ? "jobsite:w-full jobsite:md:w-1/2"
                  : "jobsite:w-full jobsite:max-w-3xl jobsite:mx-auto",
              )}
            >
              <h2
                className={cn(
                  "jobsite:font-heading jobsite:mb-5 jobsite:text-foreground",
                  headingSizeClasses[headingSize],
                )}
              >
                {heading}
              </h2>
              <div className="jobsite:space-y-4 jobsite:text-lg jobsite:leading-relaxed jobsite:text-muted-foreground">
                {paragraphs ? (
                  paragraphs.map((paragraph, idx) => <p key={idx}>{paragraph}</p>)
                ) : (
                  <p>{content}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
